import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { format, addDays } from 'date-fns';
import useCountUp from '../hooks/useCountUp';
import { localDateStr, contactAge } from '../utils/dateUtils';
import { fr } from 'date-fns/locale';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useContactPreview } from '../context/ContactPreviewContext';
import { getAvatarStyle } from '../utils/avatar';
import { api } from '../services/api';
import GenieTip from '../components/GenieTip';
import './Birthdays.css';


// ── Helpers ───────────────────────────────────────────────────────────────────
const todayStr = localDateStr();

const STATUS_STYLE = {
  read:      { dot: '#FBBF24', text: '#92400E', bg: '#FFFBEB' },
  sent:      { dot: '#34D399', text: '#065F46', bg: '#F0FDF4' },
  sending:   { dot: '#A78BFA', text: '#5B21B6', bg: '#F5F3FF' },
  scheduled: { dot: '#93C5FD', text: '#1E40AF', bg: '#EFF6FF' },
  sms:       { dot: '#34D399', text: '#065F46', bg: '#F0FDF4' },
  failed:    { dot: '#F87171', text: '#991B1B', bg: '#FEF2F2' },
  none:      { dot: '#D1D5DB', text: '#6B7280', bg: '#F9FAFB' },
};

function TrackChip({ icon, label, type }) {
  const s = STATUS_STYLE[type] || STATUS_STYLE.scheduled;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      border: '1px solid', borderColor: s.dot + '55',
      borderRadius: 20, padding: '3px 9px',
      fontSize: 12, fontWeight: 600,
      color: s.text, background: s.bg, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
      {icon} {label}
    </span>
  );
}

function getSmsStatus(c) {
  const st = c.birthday_sms_status ?? null;
  const at = c.birthday_sms_sent_at ?? null;
  if (!c.phone) return null;
  if (c.sms_opted_out) return { type: 'none', icon: '📵', label: 'SMS désactivé' };
  if (st === 'sent') {
    const t = at ? new Date(String(at).replace(' ', 'T')).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' }) : null;
    return { type: 'sms', icon: '📱', label: t ? `SMS à ${t}` : 'SMS envoyé' };
  }
  if (st === 'failed') return { type: 'failed', icon: '📱', label: 'SMS échoué' };
  return { type: 'sending', icon: '📱', label: 'SMS à 7h00' };
}

function getConseil(c) {
  if (c.ai_conseil) return c.ai_conseil;
  const age = getAge(c.birth_date);
  const isRound = age !== null && age % 10 === 0;
  const isVip = c.category === 'VIP';
  if (isRound && isVip) return `${c.first_name} fête ses ${age} ans — un cap symbolique — et fait partie de tes contacts importants. Un appel de 2 minutes créera beaucoup plus de lien.`;
  if (isRound) return `${c.first_name} fête ses ${age} ans. Un appel personnel ferait vraiment la différence pour un anniversaire aussi symbolique.`;
  if (isVip) return `${c.first_name} est un contact important. Ta présence humaine aujourd'hui renforcerait vraiment la relation.`;
  return null;
}

function fmtSentTime(dt) {
  if (!dt) return null;
  try {
    const d = new Date(String(dt).replace(' ', 'T'));
    if (isNaN(d.getTime())) return null;
    return d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
  } catch { return null; }
}

function getAge(birthDate) {
  return contactAge(birthDate);
}

function bdayThisYear(contact) {
  if (!contact.birth_date) return null;
  return `${todayStr.slice(0, 4)}-${contact.birth_date.slice(5, 10)}`;
}

function getAutoStatus(contact, isToday = false) {
  const hasEmail = !!contact.email;
  const hasPhone = !!contact.phone;
  const emailDate  = contact.last_email_sent?.slice(0, 10);
  const openDate   = contact.last_email_opened?.slice(0, 10);
  const bday       = bdayThisYear(contact);
  const wasSent    = emailDate && (emailDate === todayStr || emailDate === bday);
  const wasOpened  = openDate  && (openDate  === todayStr || openDate  === bday);
  const logStatus  = contact.birthday_email_status ?? null;
  const logSentAt  = contact.birthday_email_sent_at ?? null;
  const logOpenedAt= contact.birthday_email_opened_at ?? null;
  const logOpens   = contact.birthday_email_opens ?? 0;

  if (!hasEmail && !hasPhone) return { label: 'Injoignable', icon: '😶', type: 'none' };
  if (!hasEmail)              return { label: 'Via SMS',      icon: '💬', type: 'sms'  };
  if (logStatus === 'failed') return { label: 'Échec envoi',  icon: '❌', type: 'none' };

  if (isToday) {
    if (logOpens > 0 || logStatus === 'opened' || wasOpened) {
      const t = fmtSentTime(logOpenedAt || contact.last_email_opened);
      return { label: t ? `Lu à ${t}` : 'Lu ✓', icon: '💛', type: 'read' };
    }
    if (logStatus === 'sent' || wasSent) {
      const t = fmtSentTime(logSentAt || contact.last_email_sent);
      return { label: t ? `Envoyé ${t}` : 'Envoyé ✓', icon: '✅', type: 'sent' };
    }
    return { label: 'Envoi à 7h00', icon: '📧', type: 'sending' };
  }
  return { label: 'Programmé', icon: '📧', type: 'scheduled' };
}

// ── Skeleton loader ───────────────────────────────────────────────────────────
function BdaySkeleton() {
  return (
    <div className="bday-skeleton-list">
      {[1, 2, 3].map(i => (
        <div key={i} className="bday-skeleton-row" style={{ animationDelay: `${i * 80}ms` }}>
          <div className="bday-sk bday-sk-date" />
          <div className="bday-sk bday-sk-avatar" />
          <div className="bday-sk-body">
            <div className="bday-sk bday-sk-name" />
            <div className="bday-sk bday-sk-meta" />
          </div>
          <div className="bday-sk bday-sk-badge" />
        </div>
      ))}
    </div>
  );
}

// ── Birthday card ─────────────────────────────────────────────────────────────
function BdayCard({ c, isToday, index }) {
  const { openPreview } = useContactPreview();
  const { tenant, smtp } = useApp();

  // 📡 Canal des vœux AUTOMATIQUES pour CE contact — même logique que le
  // backend (SendBirthdayEmail) : e-mail si adresse + (SMTP vérifié OU relais
  // Appee activé) ; SMS si option anniversaire active + numéro + crédits + pas de refus.
  const emailAuto = !!c.email && (smtp?.verified || tenant?.settings?.birthday_fallback_mailjet);
  const smsAuto   = !!c.phone && (tenant?.settings?.sms?.birthday_sms_enabled)
    && ((tenant?.sms_credits ?? 0) > 0) && !c.sms_opted_out && c.sms_consent !== false;
  const autoChannelChip = emailAuto && smsAuto
    ? { type: 'scheduled', icon: '📧📱', label: 'Vœux auto : e-mail + SMS' }
    : emailAuto
      ? { type: 'scheduled', icon: '📧', label: 'Vœux auto par e-mail' }
      : smsAuto
        ? { type: 'scheduled', icon: '📱', label: 'Vœux auto par SMS' }
        : { type: 'none', icon: '🕳', label: 'Pas d\'envoi auto possible' };
  const today   = new Date();
  const age     = getAge(c.birth_date);
  const isRound = age && age % 10 === 0;
  const isVip   = c.category === 'VIP';
  const autoSt  = getAutoStatus(c, isToday);
  const needsCall = isVip || isRound;
  const smsStatus = getSmsStatus(c);
  const conseil   = getConseil(c);

  const [, m, d] = c.birth_date.split('-');
  const bday = new Date(today.getFullYear(), parseInt(m) - 1, parseInt(d));
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const diff = Math.round((bday - todayMidnight) / (1000 * 3600 * 24));

  const isSpecial = isRound || isVip;

  return (
    <div
      className={[
        'bday-card2',
        isToday          ? 'bday-card2--today'   : '',
        isSpecial        ? 'bday-card2--special'  : '',
        autoSt.type === 'read' ? 'bday-card2--read' : '',
      ].join(' ')}
      style={{ animationDelay: `${index * 55}ms` }}
    >
      {/* Ruban : VIP uniquement — l'âge rond vit sur l'avatar (rosace dorée) */}
      {isVip && (
        <div className="bday-ribbon">
          {isRound ? '🌟 VIP' : '⭐ VIP'}
        </div>
      )}

      {/* Date bubble */}
      <div className={`bday-date-bubble ${isToday ? 'bday-date-bubble--today' : ''}`}>
        <span className="bday-bubble-day">{format(bday, 'd')}</span>
        <span className="bday-bubble-month">{format(bday, 'MMM', { locale: fr }).toUpperCase()}</span>
        {!isToday && diff > 0 && diff <= 7 && (
          <span className="bday-bubble-diff">{diff}j</span>
        )}
      </div>

      {/* Avatar — rosace dorée radar + badge âge pour les âges ronds */}
      <div className={`db2-avatar-wrap${isRound ? ' db2-avatar-wrap--round' : ''}`}>
        <div className="bday-avatar2" style={getAvatarStyle(c.first_name)}>
          {(c.first_name || '?').charAt(0).toUpperCase()}{(c.last_name || '').charAt(0).toUpperCase()}
        </div>
        {isRound && <span className="db2-avatar-age">{age}</span>}
      </div>

      {/* Main info */}
      <div className="bday-info2">
        <div className="bday-name2">
          {c.first_name} {c.last_name}
          {age && <span className="bday-age-chip">{age} ans</span>}
          {c.city && <span className="bday-city-chip">📍 {c.city}</span>}
        </div>

        {/* Relationship memory */}
        {c.relationship_memory && (
          <div className="bday-memory2">
            <span>💬</span>
            <span>{c.relationship_memory.length > 90 ? c.relationship_memory.slice(0, 90) + '…' : c.relationship_memory}</span>
          </div>
        )}

        {/* Action buttons */}
        <div className="bday-actions2">
          {c.phone && (
            <a href={`tel:${c.phone}`} className="bday-btn2 bday-btn2--call">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.42 2 2 0 0 1 3.6 1.25h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.4A16 16 0 0 0 15.6 16.09l.91-.92a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              Appeler
            </a>
          )}
          {c.phone && (
            <a
              href={`sms:${c.phone}?body=${encodeURIComponent(`Bonjour ${c.first_name}, je te souhaite un très bel anniversaire 🎂 !`)}`}
              className="bday-btn2 bday-btn2--sms"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              SMS
            </a>
          )}
          {c.email && (
            <a href={`mailto:${c.email}`} className="bday-btn2 bday-btn2--email">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              E-mail
            </a>
          )}
          <button onClick={() => openPreview(c.id)} className="bday-btn2 bday-btn2--view">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            Fiche
          </button>
        </div>
      </div>

      {/* Status column */}
      <div className="bday-status2-col">
        {isToday ? (
          <>
            {/* Aujourd'hui : les VRAIS statuts d'envoi */}
            <TrackChip icon={autoSt.icon} label={autoSt.label} type={autoSt.type} />
            {smsStatus && <TrackChip icon={smsStatus.icon} label={smsStatus.label} type={smsStatus.type} />}
          </>
        ) : (
          /* À venir : ce qui partira automatiquement, et par quel canal */
          <TrackChip icon={autoChannelChip.icon} label={autoChannelChip.label} type={autoChannelChip.type} />
        )}
        {/* Appel conseillé */}
        {needsCall && <TrackChip icon="📞" label="Appel conseillé" type="scheduled" />}
      </div>

      {/* Conseil Appee — affiché si VIP ou âge rond */}
      {conseil && (
        <div className="bday-conseil-strip">
          <span className="bday-conseil-icon">💡</span>
          <span className="bday-conseil-text"><strong>Mon conseil : </strong>{conseil}</span>
        </div>
      )}
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyBirthdays({ tab }) {
  const configs = {
    today: { emoji: '😌', title: 'Aucun anniversaire aujourd\'hui', sub: 'J\'ai vérifié — tu peux souffler. Je reste en veille pour les prochains jours 👁️' },
    week:  { emoji: '📅', title: 'Semaine calme cette semaine', sub: 'Pas d\'anniversaire dans les 7 prochains jours. Profites-en pour préparer tes campagnes.' },
    month: { emoji: '🗓', title: 'Aucun anniversaire ce mois-ci', sub: 'Ajoute des dates de naissance dans les fiches contacts et je m\'occupe du reste — promis.' },
  };
  const c = configs[tab] || configs.month;
  return (
    <div className="bday-empty">
      <div className="bday-empty-emoji">{c.emoji}</div>
      <div className="bday-empty-title">{c.title}</div>
      <p className="bday-empty-sub">{c.sub}</p>
      <Link to="/contacts" className="bday-empty-cta">Gérer mes contacts →</Link>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Birthdays() {
  const [searchParams] = useSearchParams();
  const { openPreview } = useContactPreview();
  const [tab, setTab] = useState(() => {
    const t = searchParams.get('tab');
    return ['today', 'week', 'month'].includes(t) ? t : 'today';
  });
  const [prevTab, setPrevTab] = useState(tab);
  const [animDir, setAnimDir] = useState('right');
  const [transitioning, setTransitioning] = useState(false);

  const [bdayLists, setBdayLists] = useState({ today: [], week: [], month: [], monthTotal: 0 });
  const [loading, setLoading] = useState(true);

  const loadBirthdays = useCallback(async () => {
    try {
      const data = await api.getBirthdays();
      setBdayLists({
        today:      data.today || [],
        week:       data.week  || [],
        month:      data.month || [],
        monthTotal: data.month_total ?? (data.month || []).length,
      });
    } catch (err) {
      console.error('Birthday load failed', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBirthdays(); }, [loadBirthdays]);

  // Animated tab switch
  const tabOrder = ['today', 'week', 'month'];
  const handleTabChange = (next) => {
    if (next === tab) return;
    const dir = tabOrder.indexOf(next) > tabOrder.indexOf(tab) ? 'right' : 'left';
    setAnimDir(dir);
    setTransitioning(true);
    setTimeout(() => {
      setTab(next);
      setTransitioning(false);
    }, 180);
  };

  const today      = new Date();
  const isToday    = tab === 'today';
  const current    = bdayLists[tab] ?? [];
  const monthTotal = bdayLists.monthTotal;

  // Count-up values for hero
  const cntToday = useCountUp(bdayLists.today.length);
  const cntWeek  = useCountUp(bdayLists.week.length);
  const cntMonth = useCountUp(monthTotal);

  // Auto-sent today
  const autoSentToday = bdayLists.today.filter(c => {
    const d = c.last_email_sent?.slice(0, 10);
    return d && (d === todayStr || d === bdayThisYear(c));
  }).length;

  const autoReadToday = bdayLists.today.filter(c => {
    const d = c.last_email_opened?.slice(0, 10);
    return d && (d === todayStr || d === bdayThisYear(c));
  }).length;

  const TAB_LABELS = {
    today: `Aujourd'hui · ${format(today, 'd MMMM', { locale: fr })}`,
    week:  `Cette semaine · ${format(today, 'd MMM', { locale: fr })} → ${format(addDays(today, 7), 'd MMM', { locale: fr })}`,
    month: `Ce mois · ${(format(today, 'MMMM yyyy', { locale: fr })[0].toUpperCase() + format(today, 'MMMM yyyy', { locale: fr }).slice(1))}`,
  };

  return (
    <div className="bday-page">

      {/* ── Hero header ── */}
      <div className="bday-hero bday-animate-in" style={{ animationDelay: '0ms' }}>
        <div className="bday-hero-nebula" />
        <div className="bday-hero-top">
          <div className="bday-hero-left">
            <div className="bday-hero-icon-wrap">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.95)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
                <path d="M12 3v2M8 3l1 2M16 3l-1 2"/>
              </svg>
            </div>
            <div>
              <h1 className="bday-hero-title">Anniversaires</h1>
              <p className="bday-hero-sub">Appee surveille toutes les dates — tu n'oublies plus jamais un anniversaire client</p>
            </div>
          </div>
          {bdayLists.today.length > 0 && (
            <div className="bday-today-badge">
              <span className="bday-today-badge-dot" />
              <span>{bdayLists.today.length} anniversaire{bdayLists.today.length > 1 ? 's' : ''} aujourd'hui</span>
            </div>
          )}
        </div>
        <GenieTip
          id="birthdays"
          title="🎂 Les anniversaires clients"
          foot={<>Un anniversaire souhaité = un client qui se sent unique. Active le SMS anniversaire pour ~98 % de lecture.</>}
        >
          Appee surveille toutes les dates et envoie le message le jour J (e-mail, + SMS si activé). Tu n'oublies plus jamais personne.
        </GenieTip>

        {/* Celebration message when there are today birthdays */}
        {bdayLists.today.length > 0 && (() => {
          // Statut honnête selon l'heure + « lu par » nominatif
          const readNames = bdayLists.today
            .filter(c => { const d = c.last_email_opened?.slice(0, 10); return d && (d === todayStr || d === bdayThisYear(c)); })
            .map(c => `${c.first_name} ${(c.last_name || '').charAt(0)}.`.trim());
          const past7 = new Date().getHours() >= 7;
          let statusTxt;
          if (autoSentToday > 0) {
            statusTxt = ` ✅ ${autoSentToday} message${autoSentToday > 1 ? 's' : ''} envoyé${autoSentToday > 1 ? 's' : ''} à 7h00`;
            if (readNames.length > 0) statusTxt += ` · 👀 lu par ${readNames.join(', ')} 💛`;
          } else if (!past7) {
            statusTxt = ' Les messages sont prêts — ils partiront à 7h00.';
          } else {
            statusTxt = ' ⏳ Pas encore envoyés (ajoutés après le passage de 7h00 ?) — envoie-leur un mot en un clic ci-dessous.';
          }
          return (
            <div className="bday-hero-alert">
              <span className="bday-hero-alert-icon">🎉</span>
              <div className="bday-hero-alert-text">
                <strong>{bdayLists.today.length} anniversaire{bdayLists.today.length > 1 ? 's' : ''} aujourd'hui !</strong>
                {statusTxt}
              </div>
            </div>
          );
        })()}

        <div className="bday-summary-grid">
          {[
            {
              key: 'today',
              icon: '🎉',
              label: "Aujourd'hui",
              count: cntToday,
              real: bdayLists.today.length,
              grad: 'linear-gradient(135deg, #6428F4 0%, #C93DB0 55%, #FF7A1A 100%)',
              shadow: 'rgba(100,40,244,.35)',
            },
            {
              key: 'week',
              icon: '📅',
              label: 'Cette semaine',
              count: cntWeek,
              real: bdayLists.week.length,
              grad: 'linear-gradient(135deg, #5A35FF 0%, #7C3AED 100%)',
              shadow: 'rgba(90,53,255,.25)',
            },
            {
              key: 'month',
              icon: '🗓',
              label: 'Ce mois',
              count: cntMonth,
              real: monthTotal,
              grad: 'linear-gradient(135deg, #0EA5E9 0%, #6366F1 100%)',
              shadow: 'rgba(14,165,233,.2)',
            },
          ].map(s => (
            <button
              key={s.key}
              className={`bday-sum-card ${tab === s.key ? 'bday-sum-card--active' : ''}`}
              onClick={() => handleTabChange(s.key)}
              style={tab === s.key ? { boxShadow: `0 8px 28px ${s.shadow}` } : {}}
            >
              {/* Background gradient shown when active */}
              <div
                className="bday-sum-card-bg"
                style={{ background: s.grad, opacity: tab === s.key ? 1 : 0 }}
              />
              <span className="bday-sum-icon">{s.icon}</span>
              <div className="bday-sum-info">
                <div className={`bday-sum-count ${tab === s.key ? 'bday-sum-count--active' : ''}`}>
                  {s.count}
                </div>
                <div className="bday-sum-label">{s.label}</div>
              </div>
              {s.real > 0 && tab !== s.key && (
                <div className="bday-sum-dot" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Card list ── */}
      <div className="bday-list-wrap bday-animate-in" style={{ animationDelay: '160ms' }}>
        {/* Header */}
        <div className={`bday-list-head bday-list-head--${tab}`}>
          <div className="bday-list-head-inner">
            <span className="bday-list-head-icon">
              {tab === 'today' ? '🎉' : tab === 'week' ? '📅' : '🗓'}
            </span>
            <div>
              <div className="bday-list-head-title">{TAB_LABELS[tab]}</div>
              <div className="bday-list-head-sub">
                {tab === 'month' ? monthTotal : current.length} anniversaire{(tab === 'month' ? monthTotal : current.length) !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          {/* Tab pills inside header */}
          <div className="bday-tab-pills">
            {['today', 'week', 'month'].map(t => (
              <button
                key={t}
                className={`bday-tab-pill ${tab === t ? 'bday-tab-pill--active' : ''}`}
                onClick={() => handleTabChange(t)}
              >
                {t === 'today' ? "Aujourd'hui" : t === 'week' ? 'Semaine' : 'Mois'}
                {bdayLists[t]?.length > 0 && (
                  <span className="bday-tab-pill-count">
                    {t === 'month' ? monthTotal : bdayLists[t].length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Barre de progression supprimée — redondante avec le bandeau du hero
            (statut honnête + « lu par » nominatif) et rendu cassé à 0%. */}

        {/* Content */}
        <div className={`bday-list-body ${transitioning ? `bday-list-body--exit-${animDir}` : 'bday-list-body--enter'}`}>
          {loading ? (
            <BdaySkeleton />
          ) : current.length === 0 ? (
            <EmptyBirthdays tab={tab} />
          ) : (
            <div className="bday-cards2">
              {current.map((c, i) => (
                <BdayCard key={c.id} c={c} isToday={isToday} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
